import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
  GoogleAuthProvider,
  signInWithPopup
} from 'firebase/auth';
import { auth, db } from '../config/firebase';
import { doc, getDoc, setDoc, collection, query, where, getDocs, serverTimestamp } from 'firebase/firestore';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isEditor, setIsEditor] = useState(false);

  // Normal kullanıcı girişi
  const login = async (email, password) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const userDoc = await getDoc(doc(db, 'users', userCredential.user.uid));
      const userData = userDoc.data();

      // Kullanıcı engellenmiş mi kontrolü
      if (userData?.isBanned) {
        await signOut(auth); // Engelli kullanıcıyı otomatik çıkış yaptır
        throw new Error(`Hesabınız engellenmiştir. Sebep: ${userData.banReason || 'Belirtilmemiş'}`);
      }

      return { ...userCredential.user, role: userData?.role };
    } catch (error) {
      if (error.code === 'auth/too-many-requests') {
        throw new Error('Çok fazla başarısız giriş denemesi yapıldı. Lütfen bir süre bekleyip tekrar deneyin.');
      } else if (error.code === 'auth/wrong-password') {
        throw new Error('Hatalı şifre');
      } else if (error.code === 'auth/user-not-found') {
        throw new Error('Kullanıcı bulunamadı');
      } else if (error.code === 'auth/invalid-email') {
        throw new Error('Geçersiz email adresi');
      }
      throw error;
    }
  };

  // Kayıt olma fonksiyonu
  const register = async (email, password, displayName) => {
    try {
      // Önce email'in kullanımda olup olmadığını kontrol et
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('email', '==', email));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        throw new Error('Bu e-posta adresi zaten kullanımda.');
      }

      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      
      await updateProfile(userCredential.user, {
        displayName: displayName
      });

      await setDoc(doc(db, 'users', userCredential.user.uid), {
        email,
        displayName,
        role: 'user',
        createdAt: new Date().toISOString()
      });

      return userCredential.user;
    } catch (error) {
      if (error.code === 'auth/email-already-in-use') {
        throw new Error('Bu e-posta adresi zaten kullanımda.');
      }
      switch (error.code) {
        case 'auth/invalid-email':
          throw new Error('Geçersiz e-posta adresi.');
        case 'auth/weak-password':
          throw new Error('Şifre çok zayıf.');
        default:
          throw new Error('Kayıt olurken bir hata oluştu: ' + error.message);
      }
    }
  };

  // Kullanıcı durumu değişikliğini dinle
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();

            // Kullanıcı engellenmiş mi kontrolü
            if (userData.isBanned) {
              await signOut(auth); // Engelli kullanıcıyı otomatik çıkış yaptır
              setUser(null);
              setIsAdmin(false);
              setIsEditor(false);
              return;
            }

            const updatedUser = { ...user, role: userData.role };
            setUser(updatedUser);
            setIsAdmin(userData.role === 'admin');
            setIsEditor(userData.role === 'editor');

            // Son giriş zamanını güncelle
            await setDoc(doc(db, 'users', user.uid), {
              lastLoginAt: serverTimestamp()
            }, { merge: true });
          } else {
            await signOut(auth);
            setUser(null);
            setIsAdmin(false);
            setIsEditor(false);
          }
        } catch (error) {
          console.error("Kullanıcı bilgileri alınamadı:", error);
          await signOut(auth);
          setUser(null);
          setIsAdmin(false);
          setIsEditor(false);
        }
      } else {
        setUser(null);
        setIsAdmin(false);
        setIsEditor(false);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Admin ve editör girişi
  const loginAsAdminOrEditor = async (email, password) => {
    try {
      // Normal giriş yap
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      
      // Kullanıcı bilgilerini al
      const userDoc = await getDoc(doc(db, 'users', userCredential.user.uid));
      
      if (!userDoc.exists()) {
        // Eğer kullanıcı dokümanı yoksa, admin email'i ile eşleşiyorsa admin olarak oluştur
        if (email === process.env.REACT_APP_ADMIN_EMAIL) {
          await setDoc(doc(db, 'users', userCredential.user.uid), {
            email: email,
            role: 'admin',
            displayName: 'Admin',
            createdAt: new Date().toISOString()
          });
          return { ...userCredential.user, role: 'admin' };
        }
        await signOut(auth);
        throw new Error('Kullanıcı bilgileri bulunamadı');
      }

      const userData = userDoc.data();

      // Rol kontrolü
      if (!userData.role || (userData.role !== 'admin' && userData.role !== 'editor')) {
        await signOut(auth);
        throw new Error('Bu alana erişim yetkiniz bulunmamaktadır');
      }

      // Son giriş zamanını güncelle
      await setDoc(doc(db, 'users', userCredential.user.uid), {
        lastLoginAt: serverTimestamp()
      }, { merge: true });

      // Başarılı giriş
      return { ...userCredential.user, role: userData.role };
    } catch (error) {
      // Hata mesajlarını Türkçeleştir
      if (error.code === 'auth/wrong-password') {
        throw new Error('Hatalı şifre');
      }
      if (error.code === 'auth/user-not-found') {
        throw new Error('Kullanıcı bulunamadı');
      }
      if (error.code === 'auth/invalid-email') {
        throw new Error('Geçersiz email adresi');
      }
      if (error.code === 'auth/too-many-requests') {
        throw new Error('Çok fazla başarısız giriş denemesi yapıldı. Lütfen bir süre bekleyip tekrar deneyin.');
      }
      throw error;
    }
  };

  const signInWithGoogle = async () => {
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      
      // Kullanıcı dokümanını kontrol et
      const userRef = doc(db, 'users', result.user.uid);
      const userDoc = await getDoc(userRef);

      let userData = {
        email: result.user.email,
        displayName: result.user.displayName,
        photoURL: result.user.photoURL,
        lastLogin: serverTimestamp()
      };

      if (userDoc.exists()) {
        // Mevcut kullanıcı - engel kontrolü yap
        const existingData = userDoc.data();
        if (existingData.isBanned) {
          await signOut(auth);
          throw new Error(`Hesabınız engellenmiştir. Sebep: ${existingData.banReason || 'Belirtilmemiş'}`);
        }
        userData = {
          ...existingData,
          ...userData
        };
      } else {
        // Yeni kullanıcı
        userData = {
          ...userData,
          role: 'user',
          createdAt: serverTimestamp(),
          isBanned: false
        };
      }

      await setDoc(userRef, userData, { merge: true });

      return {
        ...result.user,
        role: userData.role
      };
    } catch (error) {
      console.error('Google ile giriş hatası:', error);
      throw error;
    }
  };

  const logout = () => {
    return signOut(auth);
  };

  const value = {
    user,
    isAdmin,
    isEditor,
    login,
    register,
    loginAsAdminOrEditor,
    signInWithGoogle,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
} 